import { Component, Input, OnInit } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import {
  ExportModalOption,
  ExportAction,
  CancelAction
} from '../../types/export-modal.types';

@Component({
  templateUrl: './export-modal.component.html',
  styleUrls: ['./export-modal.styles.scss']
})
export class ExportModalComponent implements OnInit {
  @Input()
  public context: { options: ExportModalOption[] };

  private optionsSelectionSubject$: Subject<
    ExportModalOption[]
  > = new Subject();

  public options$: Observable<ExportModalOption[]>;

  public isExportDisabled$: Observable<boolean>;

  @Input()
  public response: Subject<any>;

  public ngOnInit() {
    this.options$ = this.optionsSelectionSubject$.pipe(
      startWith(this.context.options)
    );

    /* *
     * Disable export button based on option.isChekced or option.configOptions.isChecked
     * based on the value of o.updatedWithConfigOpts
     * */
    this.isExportDisabled$ = this.options$.pipe(
      map((selectedOptions) => {
        return !selectedOptions.some((o) => {
          return !o.updatedWithConfigOpts
            ? o.isChecked
            : o.configOptions.some((co) => co.isChecked);
        });
      })
    );
  }

  public exportPartOptionChecked(
    options: ExportModalOption[],
    { name, checked }: HTMLInputElement
  ): void {
    const updatedOptions = options.map((option) => {
      if (option.name !== name) {
        return option;
      }

      return {
        ...option,
        isChecked: checked,
        configOptions: option.configOptions.map((configOption) => ({
          ...configOption,
          isChecked: checked && configOption.isCheckedByDefault
        }))
      };
    });

    this.optionsSelectionSubject$.next(updatedOptions);
  }

  public exportPartConfigOptionChecked(
    exportPartName: string,
    options: ExportModalOption[],
    { name, checked }: HTMLInputElement
  ) {
    const updatedOptions = options.map((option) => {
      if (option.name !== exportPartName) {
        return option;
      }

      const updateOpt = {
        ...option,
        configOptions: option.configOptions.map((configOption) =>
          configOption.name === name
            ? { ...configOption, isChecked: checked }
            : configOption
        )
      };

      return !option.updatedWithConfigOpts
        ? updateOpt
        : this.updateAllOption(updateOpt);
    });
    this.optionsSelectionSubject$.next(updatedOptions);
  }

  private updateAllOption(updateOpt: ExportModalOption) {
    let checkAll = true;
    // Setting isChecked in option as true only if all configOptions are true
    updateOpt.configOptions.forEach((configOpt) => {
      if (configOpt.isChecked !== configOpt.isCheckedByDefault) {
        checkAll = false;
      }
    });

    return {
      ...updateOpt,
      isChecked: checkAll
    };
  }

  public export(options: ExportModalOption[]) {
    const optUpdate = options.map((opt) => {
      if (!opt.updatedWithConfigOpts) {
        return opt;
      } else {
        // Setting isChecked to true for export, if one of the config options is checked
        const isChecked = opt.configOptions.some((co) => co.isChecked);

        return {
          ...opt,
          isChecked
        };
      }
    });
    this.response.next(new ExportAction(optUpdate));
    this.response.complete();
  }

  public cancel() {
    this.response.next(new CancelAction());
    this.response.complete();
  }
}
